import { useQuery } from '@tanstack/react-query';
import { format, parseISO, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useAccessibleCalendars } from '@/hooks/useAccessibleCalendars';
import { useClientSubscriptionStore } from '@/store/api/clients/clientSubscriptionStore';
import { attachCalendarScopes } from '@/services/subscriptionCalendarsService';
import { subscriptionsCoveringCalendar, type CalendarScopedSubscription } from '@/lib/subscriptionCalendars';
import { resolveCalendarColor, softCalendarBg } from '@/lib/calendarColors';
import { useTranslation } from 'react-i18next';

interface CalendarQuotaCardsProps {
  gymId: string | null;
  userId: string | null | undefined;
  className?: string;
}

/**
 * Compteurs de séances PAR PLANNING pour un adhérent multi-abonnements.
 * Ne s'affiche que lorsque l'adhérent a accès à plusieurs calendriers — sinon
 * le compteur global existant suffit. Chaque carte reprend la couleur du
 * planning (`gym_calendars.color`) pour rester cohérente avec le sélecteur et
 * l'agenda.
 */
export function CalendarQuotaCards({ gymId, userId, className }: CalendarQuotaCardsProps) {
  const { t } = useTranslation('bookingui');
  const { accessibleCalendars } = useAccessibleCalendars(gymId);

  const { data: scoped = [] } = useQuery({
    queryKey: ['calendar-quotas', gymId, userId],
    enabled: !!gymId && !!userId && accessibleCalendars.length > 1,
    staleTime: 60_000,
    queryFn: async (): Promise<CalendarScopedSubscription[]> => {
      const subs = await useClientSubscriptionStore.getState().getClientActiveSubscriptionsByUserId(userId!);
      return (await attachCalendarScopes(subs as CalendarScopedSubscription[], gymId!)) as CalendarScopedSubscription[];
    },
  });

  // Toujours après les hooks : n'afficher que si multi-planning.
  if (accessibleCalendars.length <= 1) return null;

  const cards = accessibleCalendars.map((cal) => {
    const covering = subscriptionsCoveringCalendar(scoped, cal.id);
    const remaining = covering.reduce((n, s) => n + (s.sessions_remaining ?? 0), 0);
    const endDates = covering
      .map((s) => (s.end_date ? parseISO(s.end_date) : null))
      .filter((d): d is Date => !!d && isValid(d));
    const endDate = endDates.length ? new Date(Math.max(...endDates.map((d) => d.getTime()))) : null;
    return { cal, remaining, endDate, hasSub: covering.length > 0 };
  });

  return (
    <div className={cn('grid grid-cols-1 gap-3 sm:grid-cols-2', className)}>
      {cards.map(({ cal, remaining, endDate, hasSub }) => {
        const color = resolveCalendarColor(cal.color, cal.id);
        return (
          <div
            key={cal.id}
            className="relative overflow-hidden rounded-xl border border-border bg-card p-4 shadow-sm"
          >
            <span className="absolute inset-y-0 left-0 w-1" style={{ backgroundColor: color }} aria-hidden />
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
              <span className="truncate">{cal.name}</span>
            </div>
            {hasSub ? (
              <>
                <div className="mt-1.5 text-2xl font-bold tabular-nums tracking-tight">
                  {remaining}
                  <span className="ml-1 text-sm font-medium text-muted-foreground">{t("quota.sessions")}</span>
                </div>
                {endDate && (
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {t("quota.validUntil", { date: format(endDate, 'd MMM yyyy', { locale: fr }) })}
                  </div>
                )}
              </>
            ) : (
              <div className="mt-1.5">
                <span
                  className="inline-block rounded-full px-2.5 py-1 text-xs font-semibold"
                  style={{ color, backgroundColor: softCalendarBg(color) }}
                >
                  {t("quota.noActiveSubscription")}
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
